/** Shared, authenticated shop readiness; builder source changes reuse this cache entry. */
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  captureAuthenticatedShopBootstrap,
  getAppBridgeShop,
  normalizeShopifyShop,
} from '../authenticatedShopBootstrap'
import { reportClientDiagnostic } from '../clientDiagnostics'
import { shopifyApiFetch, type ShopifyAppBridge } from '../shopifyApiFetch'
import {
  getVerifiedTestFeedConnectionScope,
  shopifyQueryKey,
  type VerifiedTestFeedConnectionScope,
} from '../ShopifyAppContext'
import type { TestFeedConnectionStatus } from '../types'
import { readTestFeedConnectionStatus } from './shopifyConnectionStatus'
import { shopifyQueryPolicies } from '../queries/shopifyQueryPolicies'

export const shopifyConnectionQueryKey = (shopDomain: string) =>
  shopifyQueryKey(normalizeShopifyShop(shopDomain) || 'no-shop', 'no-org', 'no-connection', ['connection-readiness'])

/** A dispatched unlink remains uncertain until its response or a changed/disabled authority resolves it. */
export type ShopifyDisconnectTarget = { connectionId: string; expectedActivatedAt: string }

export type ShopifyConnectionSnapshot = {
  verifiedShop: string
  readiness: TestFeedConnectionStatus
  scope: VerifiedTestFeedConnectionScope | null
  disconnectTarget?: ShopifyDisconnectTarget
}

/** Cancel before removal: late old-period reads must not repopulate a replacement collection's cache. */
export async function evictShopifyConnectionData(queryClient: QueryClient, scope: VerifiedTestFeedConnectionScope) {
  const queryKey = shopifyQueryKey(scope.shopDomain, scope.orgId, scope.connectionId, [])
  await queryClient.cancelQueries({ queryKey })
  queryClient.removeQueries({ queryKey })
}

export async function fetchShopifyConnection(
  shopify: ShopifyAppBridge,
  expectedShop: string,
  signal: AbortSignal,
  cleanupConnectionId?: string,
): Promise<ShopifyConnectionSnapshot> {
  // The scope guard stays outside the reporting boundary below: a racing shop change
  // must reject before any authenticated request — including the diagnostic report — is sent.
  const bootstrap = await captureAuthenticatedShopBootstrap(shopify, { expectedShop, signal })
  try {
    const statusResponse = await shopifyApiFetch(bootstrap.authenticatedShopify, '/api/testfeed-connection-status', {
      init: { signal },
    })
    const verifiedShop = bootstrap.shopDomain
    let readiness = await readTestFeedConnectionStatus(statusResponse)
    // Normal active-account resolution wins. Only its authoritative absence permits recovery of
    // the exact earlier target; neither historical org order nor the URL grants merchant authority.
    if (readiness.code === 'SHOPIFY_CONNECTION_NOT_FOUND' && cleanupConnectionId) {
      const params = new URLSearchParams({ connectionId: cleanupConnectionId })
      const selected = await shopifyApiFetch(
        bootstrap.authenticatedShopify,
        `/api/testfeed-connection-status?${params}`,
        {
          init: { signal },
        },
      )
      readiness = await readTestFeedConnectionStatus(selected)
      if (readiness.connectionId !== cleanupConnectionId || readiness.externalAccountId !== verifiedShop)
        throw new Error('SHOPIFY_CONNECTION_DISCONNECT_CONFLICT: Cleanup read did not confirm the selected store')
    }
    return { verifiedShop, readiness, scope: getVerifiedTestFeedConnectionScope(readiness, verifiedShop) }
  } catch (error) {
    // The readiness card renders its Unavailable copy from this throw; report it so the
    // symptom is traceable even when the Worker's own 5xx auto-capture can't attribute it.
    if (signal.aborted) throw error
    reportClientDiagnostic(shopify, { schema: 'shopify-connection-status', paths: ['status_check_failed'] })
    throw error
  }
}

export function useShopifyConnection(shopify: ShopifyAppBridge, expectedShop: string | null | undefined) {
  const shopDomain = normalizeShopifyShop(expectedShop)
  const queryClient = useQueryClient()
  const queryKey = shopifyConnectionQueryKey(shopDomain || 'no-shop')
  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const previous = queryClient.getQueryData<ShopifyConnectionSnapshot>(queryKey)
      const params = new URLSearchParams(globalThis.window?.location.search)
      const recoveryTarget = params.get('cleanupConnectionId')?.trim()
      const expectedActivatedAt = params.get('disconnectActivatedAt')
      const disconnectTarget =
        previous?.disconnectTarget ??
        (recoveryTarget && expectedActivatedAt ? { connectionId: recoveryTarget, expectedActivatedAt } : undefined)
      const next = await fetchShopifyConnection(
        shopify,
        shopDomain!,
        signal,
        disconnectTarget?.connectionId ?? previous?.readiness.connectionId ?? recoveryTarget,
      )
      signal.throwIfAborted()
      if (
        previous?.readiness.orgId &&
        previous.readiness.connectionId &&
        (previous.readiness.connectionId !== next.readiness.connectionId ||
          previous.readiness.connectionActivatedAt !== next.readiness.connectionActivatedAt ||
          previous.readiness.collectionId !== next.readiness.collectionId)
      )
        await evictShopifyConnectionData(queryClient, {
          shopDomain: previous.verifiedShop,
          orgId: previous.readiness.orgId,
          connectionId: previous.readiness.connectionId,
        })
      signal.throwIfAborted()
      // A GET may overtake a still-running POST. The same active activation is not evidence that
      // unlink failed, even after navigation/reload. Keep its product scope quarantined and poll.
      if (
        disconnectTarget &&
        !(
          next.readiness.connectionId &&
          (next.readiness.connectionId !== disconnectTarget.connectionId ||
            (next.readiness.connectionActivatedAt &&
              next.readiness.connectionActivatedAt !== disconnectTarget.expectedActivatedAt) ||
            next.readiness.connectionStatus === 'disabled')
        )
      )
        return { ...next, scope: null, disconnectTarget }
      const browserWindow = globalThis.window
      if (browserWindow && recoveryTarget && getAppBridgeShop(shopify) === shopDomain) {
        const url = new URL(browserWindow.location.href)
        if (
          url.searchParams.get('cleanupConnectionId') === recoveryTarget &&
          url.searchParams.get('disconnectActivatedAt') === expectedActivatedAt
        ) {
          url.searchParams.delete('disconnectActivatedAt')
          if (next.scope) url.searchParams.delete('cleanupConnectionId')
          browserWindow.history.replaceState(null, '', url)
        }
      }
      return next
    },
    enabled: Boolean(shopDomain),
    ...shopifyQueryPolicies.connection,
    // Cleanup remains observable after unlink removes the connected product scope. Poll the
    // owning readiness read, not a timer/local copy on Settings; stop at proof or support-only failure.
    refetchInterval: (current) => {
      if (
        current.state.data?.disconnectTarget ||
        new URLSearchParams(globalThis.window?.location.search).has('disconnectActivatedAt')
      )
        return 5000
      const cleanup = current.state.data?.readiness.cleanup
      return cleanup?.status === 'pending' || (cleanup?.status === 'failed' && cleanup.canRetry) ? 5000 : false
    },
  })
  const params = new URLSearchParams(globalThis.window?.location.search)
  const isDisconnectUnconfirmed = Boolean(
    query.data?.disconnectTarget || (params.get('cleanupConnectionId') && params.get('disconnectActivatedAt')),
  )
  return {
    ...query,
    verifiedShop: query.data?.verifiedShop ?? null,
    readiness: query.data?.readiness ?? null,
    isDisconnectUnconfirmed,
    // A failed background read does not revoke an already admitted scope. A
    // returned disconnected authority or an explicit unlink still removes it.
    scope: isDisconnectUnconfirmed ? null : (query.data?.scope ?? null),
  }
}
